import { Layout, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import './App.css';

// Import components
import Header from './components/Header';
import HomePage from './components/HomePage';
import ServiceDisplay from './components/ServiceDisplay';
import Sidebar from './components/Sidebar';
import SignInPage from './components/SignInPage';
import SignOutPage from './components/SignOutPage';

// Import services
import authService from './services/authService';

// Import logos
import airbyteLogo from './assets/logos/airbyte.svg';
import airflowLogo from './assets/logos/airflow.svg';
import mlflowLogo from './assets/logos/mlflow.svg';

const { Content } = Layout;

// Service definitions
const services = [
  {
    id: 'airflow',
    name: 'Airflow',
    description: 'Apache Airflow is a platform to programmatically author, schedule, and monitor workflows.',
    url: '/airflow/',
    available: true,
    logo: airflowLogo
  },
  {
    id: 'airbyte',
    name: 'Airbyte',
    description: 'Open-source data integration platform for building ELT pipelines.',
    url: '/airbyte/',
    available: true,
    logo: airbyteLogo
  },
  {
    id: 'mlflow',
    name: 'MLflow',
    description: 'Open-source platform for tracking, managing, and deploying machine learning models.',
    url: '/mlflow/',
    available: true,
    logo: mlflowLogo
  }
];

// Special URL for auto-login
const getServiceUrl = (serviceId) => {
  return services.find(s => s.id === serviceId)?.url || '/';
};

const App = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [userInfo, setUserInfo] = useState({
    name: 'User',
    email: '',
    avatar: null
  });
  const [iframeKey, setIframeKey] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignedOut, setIsSignedOut] = useState(false);

  const withLoading = async (operation) => {
    setIsLoading(true);
    try {
      await operation();
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user info
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('signout') && urlParams.get('signout') === 'complete') {
      setIsSignedOut(true);
      setIsAuthenticated(false);
      return;
    }

    const checkAuthentication = async () => {
      try {
        setIsLoading(true);
        const userInfo = await authService.getUserInfo();
        const authenticated = !!userInfo.email;
        setIsAuthenticated(authenticated);

        if (authenticated) {
          setUserInfo({
            name: userInfo.name || 'User',
            email: userInfo.email || '',
            avatar: null
          });
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  const handleServiceSelect = (service) => {
    withLoading(() => {
      setSelectedService(service);
      setIframeKey(prevKey => prevKey + 1);
    });
  };

  const handleHomeClick = () => {
    withLoading(() => setSelectedService(null));
  };

  const handleLogin = () => {
    withLoading(authService.signIn);
  };

  const handleLogout = () => {
    withLoading(authService.signOut);
  };

  return (
    <Layout className="layout">
      <Spin
        spinning={isLoading}
        size="large"
        fullscreen
      />
      {isAuthenticated ? (
        <>
          <Header
            userInfo={userInfo}
            selectedService={selectedService}
            onLogout={handleLogout}
            isLoading={isLoading}
          />

          <Layout>
            <Sidebar
              services={services}
              selectedService={selectedService}
              onServiceSelect={handleServiceSelect}
              onHomeClick={handleHomeClick}
            />

            <Layout>
              <Content>
                <div className="site-layout-content">
                  {selectedService ? (
                    <ServiceDisplay
                      service={selectedService}
                      iframeKey={iframeKey}
                      getServiceUrl={getServiceUrl}
                    />
                  ) : (
                    <HomePage services={services} onSelectService={handleServiceSelect} />
                  )}
                </div>
              </Content>
            </Layout>
          </Layout>
        </>
      ) : (
        <Layout>
          <Content>
            <div className="site-layout-content">
              {isSignedOut ? (
                <SignOutPage onLogin={handleLogin} isLoading={isLoading} />
              ) : (
                <SignInPage onLogin={handleLogin} isLoading={isLoading} />
              )}
            </div>
          </Content>
        </Layout>
      )}
    </Layout>
  );
};

export default App;
